import * as React from "react";
import {
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
} from "recharts";

import { cn } from "@/lib/utils";

/**
 * Minimal shadcn-style chart primitives built on top of Recharts.
 *
 * Goals:
 * - Tailwind-friendly (uses CSS variables)
 * - Composable (you build your own chart with Recharts primitives)
 * - Lightweight + no extra re-render work
 */

export type ChartContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Fixed height in px.
   * If omitted, the container will size to its parent (use `h-full` etc.).
   */
  height?: number;
};

export const ChartContainer = React.memo(function ChartContainer({
  className,
  height,
  ...props
}: ChartContainerProps) {
  return (
    <div
      className={cn(
        "w-full rounded-md border border-border bg-card p-2 text-foreground",
        className
      )}
      style={{ ...(height ? { height } : null), ...props.style }}
      {...props}
    />
  );
});

export type ChartResponsiveContainerProps = React.ComponentProps<
  typeof ResponsiveContainer
>;

export function ChartResponsive(props: ChartResponsiveContainerProps) {
  // Keep wrapper in one place so all charts are consistently responsive.
  return <ResponsiveContainer width="100%" height="100%" {...props} />;
}

export type ChartTooltipProps = React.ComponentProps<typeof RechartsTooltip>;

export function ChartTooltip({
  cursor = { stroke: "hsl(var(--border))" },
  contentStyle,
  itemStyle,
  labelStyle,
  ...props
}: ChartTooltipProps) {
  return (
    <RechartsTooltip
      cursor={cursor}
      contentStyle={{
        background: "hsl(var(--popover))",
        border: "1px solid hsl(var(--border))",
        borderRadius: 8,
        color: "hsl(var(--popover-foreground))",
        ...contentStyle,
      }}
      itemStyle={{ color: "hsl(var(--popover-foreground))", ...itemStyle }}
      labelStyle={{ color: "hsl(var(--muted-foreground))", ...labelStyle }}
      {...props}
    />
  );
}

export type ChartLegendProps = React.ComponentProps<typeof RechartsLegend>;

export function ChartLegend({ wrapperStyle, ...props }: ChartLegendProps) {
  return (
    <RechartsLegend
      wrapperStyle={{
        color: "hsl(var(--muted-foreground))",
        fontSize: 12,
        ...wrapperStyle,
      }}
      {...props}
    />
  );
}

export function ChartEmptyState({
  title = "No data",
  description,
  className,
}: {
  title?: string;
  description?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex h-full w-full items-center justify-center rounded-md border border-dashed border-border bg-card",
        className
      )}
    >
      <div className="text-center">
        <div className="text-sm font-medium">{title}</div>
        {description ? (
          <div className="mt-1 text-xs text-muted-foreground">
            {description}
          </div>
        ) : null}
      </div>
    </div>
  );
}
